import errorConfusion from '@/assets/errorConfusion.jpeg';
import {ConfirmProvider} from '@/contexts/confirmModal';
import AuthenticatedRoot from '@/routes/AuthenticatedRoot';
import {ThemeProvider} from '@/ThemeProvider';
import {BrandLoader} from '@/components/brand/BrandLoader';
import {BrandRoute} from '@/pages/ENT/CortileLogin/BrandRoute';
import {CortileForgotPassword} from '@/pages/ENT/CortileLogin/CortileForgotPassword';
import {CortileSetPassword} from '@/pages/ENT/CortileLogin/CortileSetPassword';
import {BrandLogoCircle} from '@/components/brand/BrandLogoCircle';
import {BrandProvider, isBtlHost, useBrand} from '@/contexts/BrandContext';
import {init} from '@amplitude/analytics-browser';
import {
  ActionButton,
  ErrorPage,
  ReferencingLayout,
  Snackbar,
  userContext,
} from '@ark/web';
import {DocumentGenerationPaused} from '@landlord/components/DocumentGenerationPaused';
import {ReferenceSignupServiceUnavailable} from '@landlord/pages/Referencing/ReferenceSignup/ReferenceSignupServiceUnavailable';
import {AdapterLuxon} from '@mui/x-date-pickers/AdapterLuxon';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import * as Sentry from '@sentry/react';
import {ErrorBoundary, getActiveSpan} from '@sentry/react';
import {SnackbarProvider} from 'notistack';
import React, {Suspense, useEffect, useState} from 'react';
import ReactGA from 'react-ga4';
import lazyWithPreload from 'react-lazy-with-preload';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import {useIntercom} from 'react-use-intercom';
import {useClarity} from 'use-clarity';
import './App.css';
import {UserTypeProvider} from './contexts/UserTypeContext/UserTypeContext';
import {BulkCloseToaster} from './pages/ENT/Conversations/List/bulkClose';
import {DeactivationToaster} from './pages/Admin/Members/List/bulkDeactivation/DeactivationToaster';
import {AppRedirect} from './routes/AppRedirect';
import {AppStoreRedirect} from './routes/AppStoreRedirect';
import {ParamsToUUIDNavigator} from './routes/ParamsToUUIDNavigator';
import {
  attemptChunkReload,
  confirmChunkRecovery,
  hasRecentChunkRefreshingFlag,
  isChunkLoadError,
  preloadAll,
  setChunkRefreshingFlag,
} from './utils/chunkReloadOperations';

const SwitchSignupPage = lazyWithPreload(
  () => import('@landlord/pages/Login/SwitchSignup'),
);
const SigningLanding = lazyWithPreload(
  () => import('@landlord/pages/Signing/TenantSigning/TenantSigning'),
);
const FacebookAuth = lazyWithPreload(
  () => import('./pages/FacebookAuth/FacebookAuth'),
);
const StopReminders = lazyWithPreload(
  () => import('@landlord/pages/Documents/Section21/StopReminders'),
);
const PrivacyPolicy = lazyWithPreload(
  () => import('./pages/PrivacyPolicy/PrivacyPolicy'),
);

const BTLLoginPage = lazyWithPreload(
  () => import('@landlord/pages/Login/Login'),
);
const SignupPage = lazyWithPreload(
  () => import('@landlord/pages/Login/Signup'),
);

const EnterpriseLogin = lazyWithPreload(() => import('./pages/ENT/Login'));

const ResetPasswordPage = lazyWithPreload(() =>
  import('@landlord/pages/Login/ResetPassword').then(module => ({
    default: module.ResetPassword,
  })),
);
const SetPasswordPage = lazyWithPreload(
  () => import('@landlord/pages/Login/SetPassword'),
);
const TenantReferencePage = lazyWithPreload(
  () => import('@landlord/pages/Referencing/TenantReference/TenantReference'),
);
const LandlordReferencePage = lazyWithPreload(
  () =>
    import('@landlord/pages/Referencing/LandlordReference/LandlordReference'),
);
const EmployerReferencePage = lazyWithPreload(
  () =>
    import('@landlord/pages/Referencing/EmployerReference/EmployerReference'),
);
const ReferenceSignup = lazyWithPreload(
  () => import('@landlord/pages/Referencing/ReferenceSignup/ReferenceSignup'),
);
const VisitorSignIn = lazyWithPreload(() =>
  import('visitor-lib').then(module => ({
    default: module.VisitorSignIn,
  })),
);
const PublicSafetyPackPage = lazyWithPreload(
  () => import('./pages/ENT/SafetyCenter/Public/PublicSafetyPackPage'),
);
const PublicSafetyDocumentRedirect = lazyWithPreload(
  () => import('./pages/ENT/SafetyCenter/Public/PublicSafetyDocumentRedirect'),
);

const AppErrorPage = () => {
  const intercom = useIntercom();
  const {name: brandName, assets} = useBrand();
  const traceId = getActiveSpan()?.traceId;
  return (
    <ReferencingLayout background={assets.tiledBackground}>
      <ErrorPage
        imageSrc={errorConfusion}
        imageAlt="person looking under bed"
        logo={<BrandLogoCircle />}
        message={`The ${brandName} team have been notified and are working on a fix. Please try again in a moment.${
          traceId
            ? ' If you would like to report this error please use the following reference to help us solve the problem.'
            : ''
        }`}
        referenceId={traceId}
        action={
          <ActionButton
            variant="primary"
            fullWidth
            sx={{mt: 4}}
            onClick={() => {
              intercom.trackEvent('Render Error', {traceId});
              intercom.showNewMessage('');
            }}
          >
            Contact Support
          </ActionButton>
        }
      />
    </ReferencingLayout>
  );
};

const ChunkErrorFallback = () => {
  const [shouldReload] = useState(() => attemptChunkReload());

  useEffect(() => {
    if (shouldReload) {
      setChunkRefreshingFlag();
      window.location.reload();
    } else {
      Sentry.captureMessage(
        'Chunk reload recovery exhausted — showing error page after max reload attempts',
        'error',
      );
    }
  }, [shouldReload]);

  if (!shouldReload) return <AppErrorPage />;
  return null;
};

const ChunkBoundaryCommitSentinel = ({
  onBoundaryCommit,
}: {
  onBoundaryCommit: () => void;
}) => {
  useEffect(() => {
    onBoundaryCommit();
  }, [onBoundaryCommit]);
  return null;
};

const CHUNK_RECOVERY_SETTLE_MS = 10000;

const componentsToPreload = [
  SwitchSignupPage,
  SigningLanding,
  FacebookAuth,
  StopReminders,
  PrivacyPolicy,
  SignupPage,
  ResetPasswordPage,
  SetPasswordPage,
  TenantReferencePage,
  LandlordReferencePage,
  EmployerReferencePage,
  BTLLoginPage,
  EnterpriseLogin,
  ReferenceSignup,
  VisitorSignIn,
];

type Props = {
  children: React.ReactNode;
};
const PrivateRoute = ({children}: Props) => {
  const location = useLocation();
  const {isLoggedIn, sessionExpired} = React.useContext(userContext);
  const searchProps = location?.search.toString()
    ? `&${location?.search.toString().split('?')[1]}`
    : '';

  const nextPath = location?.pathname;
  const fromTypeform =
    nextPath?.startsWith('/agreements') ||
    nextPath?.startsWith('/documents/section-21');
  const loginPath = sessionExpired ? '/login?reason=expired&' : '/login?';
  const redirectPath = fromTypeform ? '/typeform-signup?' : loginPath;

  if (!isLoggedIn) {
    return (
      <Navigate to={`${redirectPath}next=${nextPath}${searchProps}`} replace />
    );
  }

  return children;
};

const GA_ID = import.meta.env.VITE_GA_ID;
if (GA_ID) {
  ReactGA.initialize(`${GA_ID}`, {
    gaOptions: {siteSpeedSampleRate: 100},
  });
}
function App() {
  const {boot, update, trackEvent} = useIntercom();
  const {currentSessionUrl, clarityUserId} = useClarity();
  useEffect(() => {
    boot();
  }, [boot]);
  useEffect(() => {
    preloadAll(componentsToPreload);
  }, []);
  if (import.meta.env.VITE_AMPLITUDE_API_KEY_BTL) {
    init(import.meta.env.VITE_AMPLITUDE_API_KEY_BTL);
  }

  React.useEffect(() => {
    if (currentSessionUrl) {
      trackEvent('session', {
        link: currentSessionUrl,
      });
      Sentry.setContext('clarity', {
        link: currentSessionUrl,
      });
    }
  }, [currentSessionUrl]);

  React.useEffect(() => {
    if (clarityUserId) {
      update({
        customAttributes: {
          clarity_id: clarityUserId,
        },
      });
      Sentry.setTag('clarity.usedId', clarityUserId);
    }
  }, [clarityUserId]);

  const [recoveredFromChunkRefresh] = useState(hasRecentChunkRefreshingFlag);
  const chunkErrorSinceMountRef = React.useRef(false);
  const [boundaryCommitted, setBoundaryCommitted] = useState(false);
  const handleBoundaryCommit = React.useCallback(
    () => setBoundaryCommitted(true),
    [],
  );

  React.useEffect(() => {
    if (!recoveredFromChunkRefresh || !boundaryCommitted) return;
    const timeoutId = window.setTimeout(() => {
      if (!chunkErrorSinceMountRef.current) confirmChunkRecovery();
    }, CHUNK_RECOVERY_SETTLE_MS);
    return () => window.clearTimeout(timeoutId);
  }, [recoveredFromChunkRefresh, boundaryCommitted]);

  const renderEntLogin = !isBtlHost();

  return (
    <BrandProvider>
      <ThemeProvider>
        <ErrorBoundary
          fallback={({error}) =>
            isChunkLoadError(error) ? <ChunkErrorFallback /> : <AppErrorPage />
          }
          onError={error => {
            if (isChunkLoadError(error)) {
              chunkErrorSinceMountRef.current = true;
              Sentry.addBreadcrumb({
                category: 'chunk-load',
                message: 'Chunk load error caught by ErrorBoundary',
                level: 'warning',
              });
            }
          }}
        >
          <LocalizationProvider
            dateAdapter={AdapterLuxon}
            adapterLocale="en-gb"
          >
            <ConfirmProvider>
              <SnackbarProvider
                maxSnack={4}
                Components={{
                  default: Snackbar,
                  success: Snackbar,
                  error: Snackbar,
                  warning: Snackbar,
                  info: Snackbar,
                }}
              >
                <DeactivationToaster />
                <BulkCloseToaster />
                <BrowserRouter basename={import.meta.env.BASE_URL}>
                  <Suspense fallback={<BrandLoader />}>
                    <ChunkBoundaryCommitSentinel
                      onBoundaryCommit={handleBoundaryCommit}
                    />
                    <Routes>
                      <Route
                        path="/login"
                        element={
                          renderEntLogin ? (
                            <EnterpriseLogin />
                          ) : (
                            <BTLLoginPage />
                          )
                        }
                      />
                      <Route path="/signup" element={<SignupPage />} />
                      <Route
                        path="/typeform-signup"
                        element={
                          <DocumentGenerationPaused documentType="both" />
                        }
                      />
                      <Route
                        path="/reset-password/:uid/:token"
                        element={
                          <ParamsToUUIDNavigator parameterNames={['uid']}>
                            <BrandRoute
                              cortile={<CortileSetPassword />}
                              ark={<SetPasswordPage />}
                            />
                          </ParamsToUUIDNavigator>
                        }
                      />
                      <Route
                        path="/reset-password"
                        element={
                          <BrandRoute
                            cortile={<CortileForgotPassword />}
                            ark={<ResetPasswordPage />}
                          />
                        }
                      />
                      <Route
                        path="/switch-signup"
                        element={<SwitchSignupPage />}
                      />
                      <Route
                        path="/sign/:agreementId/:signatureId"
                        element={
                          <ParamsToUUIDNavigator
                            parameterNames={['agreementId', 'signatureId']}
                          >
                            <SigningLanding />
                          </ParamsToUUIDNavigator>
                        }
                      />
                      <Route path="/facebook-auth" element={<FacebookAuth />} />
                      <Route
                        path="/stop-reminders/:section21Id"
                        element={
                          <ParamsToUUIDNavigator
                            parameterNames={['section21Id']}
                          >
                            <StopReminders />
                          </ParamsToUUIDNavigator>
                        }
                      />
                      <Route path="/privacy" element={<PrivacyPolicy />} />
                      <Route
                        path="/tenant-reference/:tenantReferenceId"
                        element={
                          <ParamsToUUIDNavigator
                            parameterNames={['tenantReferenceId']}
                          >
                            <TenantReferencePage />
                          </ParamsToUUIDNavigator>
                        }
                      />
                      <Route
                        path="/landlord-reference/:landlordReferenceId"
                        element={
                          <ParamsToUUIDNavigator
                            parameterNames={['landlordReferenceId']}
                          >
                            <LandlordReferencePage />
                          </ParamsToUUIDNavigator>
                        }
                      />
                      <Route
                        path="/employer-reference/:employerReferenceId"
                        element={
                          <ParamsToUUIDNavigator
                            parameterNames={['employerReferenceId']}
                          >
                            <EmployerReferencePage />
                          </ParamsToUUIDNavigator>
                        }
                      />
                      <Route
                        path="/reference-signup"
                        element={<ReferenceSignupServiceUnavailable />}
                      />
                      <Route
                        path="/visitor-signin/:communityId"
                        element={
                          <ParamsToUUIDNavigator
                            parameterNames={['communityId']}
                          >
                            <VisitorSignIn />
                          </ParamsToUUIDNavigator>
                        }
                      />
                      <Route
                        path="/p/safety/t/:token"
                        element={
                          <ParamsToUUIDNavigator parameterNames={['token']}>
                            <PublicSafetyPackPage />
                          </ParamsToUUIDNavigator>
                        }
                      />
                      <Route
                        path="/p/safety/t/:token/doc/:documentId"
                        element={
                          <ParamsToUUIDNavigator
                            parameterNames={['token', 'documentId']}
                          >
                            <PublicSafetyDocumentRedirect />
                          </ParamsToUUIDNavigator>
                        }
                      />
                      <Route
                        path="/p/safety/:communityId"
                        element={
                          <ParamsToUUIDNavigator
                            parameterNames={['communityId']}
                          >
                            <PublicSafetyPackPage />
                          </ParamsToUUIDNavigator>
                        }
                      />
                      <Route
                        path="/p/safety/:communityId/doc/:documentId"
                        element={
                          <ParamsToUUIDNavigator
                            parameterNames={['communityId', 'documentId']}
                          >
                            <PublicSafetyDocumentRedirect />
                          </ParamsToUUIDNavigator>
                        }
                      />
                      <Route path="/app-redirect" element={<AppRedirect />} />
                      <Route
                        path="/download-app"
                        element={<AppStoreRedirect />}
                      />
                      <Route
                        path="*"
                        element={
                          <PrivateRoute>
                            <UserTypeProvider>
                              <AuthenticatedRoot />
                            </UserTypeProvider>
                          </PrivateRoute>
                        }
                      />
                    </Routes>
                  </Suspense>
                </BrowserRouter>
              </SnackbarProvider>
            </ConfirmProvider>
          </LocalizationProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </BrandProvider>
  );
}
export default App;
